import { useMemo, useState } from 'react'
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table'
import { BadgeDollarSign } from 'lucide-react'

import { useDebounce } from '@/components/hooks/use-debounce'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Boat } from '@/types/inventory'
import { boatColumns } from '@/features/inventory/components/boat-columns'
import { SoldDateRange } from '@/features/inventory/api'
import { useBoats } from '@/features/inventory/hooks'

const columns: ColumnDef<Boat>[] = boatColumns({ showListing: false, showSold: true })

// Stable identity for the loading state: TanStack Table reruns its row models
// (and resets pagination state) whenever `data` changes identity, so a fresh
// `[]` on every render would loop forever while the query is in flight.
const EMPTY_BOATS: Boat[] = []

// Preset sold-date windows. Each maps to a [from, to] pair of YYYY-MM-DD
// strings (both sent to the API; `to` is the first day AFTER the window and
// the backend makes it exclusive).
type SoldPeriod =
  | 'all'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'last_12_months'

const SOLD_PERIODS: { value: SoldPeriod; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'last_12_months', label: 'Last 12 months' },
]

const toISODate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function soldRangeFor(period: SoldPeriod): SoldDateRange {
  const now = new Date()
  switch (period) {
    case 'this_month':
      return { from: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)), to: toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 1)) }
    case 'last_month':
      return { from: toISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)) }
    case 'this_quarter': {
      const q = Math.floor(now.getMonth() / 3)
      return { from: toISODate(new Date(now.getFullYear(), q * 3, 1)), to: toISODate(new Date(now.getFullYear(), q * 3 + 3, 1)) }
    }
    case 'last_quarter': {
      const q = Math.floor(now.getMonth() / 3) - 1
      return { from: toISODate(new Date(now.getFullYear(), q * 3, 1)), to: toISODate(new Date(now.getFullYear(), q * 3 + 3, 1)) }
    }
    case 'this_year':
      return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear() + 1}-01-01` }
    case 'last_year': {
      const y = now.getFullYear() - 1
      return { from: `${y}-01-01`, to: `${y + 1}-01-01` }
    }
    case 'last_12_months':
      return { from: toISODate(new Date(now.getFullYear(), now.getMonth() - 11, 1)), to: toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 1)) }
    default:
      return {}
  }
}

// Sold slice of the Inventory page: boats whose listing state is sold. They
// leave the Boats tab when sold and live here instead. Search and the sold
// period are local state (the URL's q param belongs to the boats tab).
export function SoldTab() {
  const [searchInput, setSearchInput] = useState('')
  const debouncedQ = useDebounce(searchInput, 300)
  const [period, setPeriod] = useState<SoldPeriod>('all')
  // Newest sales first — the natural reading for a sold-by-period view.
  const [sorting, setSorting] = useState<SortingState>([{ id: 'sold_at', desc: true }])

  const soldRange = useMemo(() => soldRangeFor(period), [period])
  const { data, isLoading, isError } = useBoats('sold', debouncedQ || undefined, undefined, soldRange)

  const table = useReactTable({
    data: data ?? EMPTY_BOATS,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search sold boats…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full sm:max-w-sm"
          />
          <Select value={period} onValueChange={(v) => setPeriod(v as SoldPeriod)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOLD_PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!isLoading && !isError && (
          <span className="text-sm text-muted-foreground">
            {data?.length ?? 0} sold boat{(data?.length ?? 0) === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-[400px] w-full" />
      ) : isError ? (
        <div className="text-destructive">Failed to load sold boats.</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    <span className="inline-flex flex-col items-center gap-2 text-muted-foreground">
                      <BadgeDollarSign className="h-6 w-6" />
                      {period === 'all' ? 'No sold boats yet.' : 'No sold boats in this period.'}
                    </span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
